import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { TenantApiError } from "@qr-menu/api-tenant";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
} from "@qr-menu/ui";
import { tenantApi } from "../lib/api";

const REQUIRED_FIELD_MESSAGE = "E-posta zorunludur.";

// Fixed, enumeration-safe copy: identical whether or not the submitted email
// belongs to an account. Never render an "account found"/"not found" branch
// around this — the backend's 202 carries no such signal, and inventing one
// client-side would defeat the point of the generic response.
const GENERIC_SUCCESS_MESSAGE =
  "Eğer bu e-posta adresiyle bir hesap varsa, parola sıfırlama bağlantısı gönderildi.";

// These three are allowed to differ from the generic success state because
// none of them depend on whether the account exists: they describe the
// state of the service itself (rate limited, mail disabled, unreachable).
const RATE_LIMITED_MESSAGE =
  "Çok fazla deneme yaptınız. Lütfen biraz sonra tekrar deneyin.";
const UNAVAILABLE_MESSAGE =
  "Parola sıfırlama şu anda kullanılamıyor. Lütfen daha sonra tekrar deneyin.";
const GENERIC_FAILURE_MESSAGE =
  "Şu anda işlem gerçekleştirilemiyor. Lütfen daha sonra tekrar deneyin.";

export function ForgotPasswordRoute() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError(REQUIRED_FIELD_MESSAGE);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await tenantApi.forgotPassword({ email: trimmedEmail });
      // One success state, full stop. Keeping the form submitted rather than
      // letting the user resubmit avoids a second visible network request
      // for what is, from the account-existence question, a no-op either way.
      setSubmitted(true);
    } catch (err) {
      let message = GENERIC_FAILURE_MESSAGE;
      if (err instanceof TenantApiError) {
        if (err.status === 429) {
          message = RATE_LIMITED_MESSAGE;
        } else if (err.status === 503) {
          message = UNAVAILABLE_MESSAGE;
        }
        // Every other TenantApiError status — 4xx validation, unexpected
        // 5xx — falls back to the generic temporary-failure message above.
        // None of these may be worded to hint at whether the account exists.
      }
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="auth-shell">
        <Card className="auth-card">
          <CardHeader>
            <CardTitle>Parolamı unuttum</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="state ok">{GENERIC_SUCCESS_MESSAGE}</div>
            <p className="hint">
              <Link to="/login">Girişe dön</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="auth-shell">
      <Card className="auth-card">
        <CardHeader>
          <CardTitle>Parolamı unuttum</CardTitle>
          <CardDescription>
            Hesabınızın e-posta adresini girin. Bu adresle bir hesap varsa
            parola sıfırlama bağlantısı içeren bir e-posta gönderilir.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="login-form" onSubmit={handleSubmit}>
            <Field label="E-posta" htmlFor="forgot-password-email">
              <input
                id="forgot-password-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                spellCheck={false}
                required
              />
            </Field>
            {error && <div className="state error">{error}</div>}
            <div className="login-actions">
              <button type="submit" className="primary" disabled={submitting}>
                {submitting ? "Gönderiliyor…" : "Bağlantı gönder"}
              </button>
            </div>
          </form>
          <p className="hint">
            <Link to="/login">Girişe dön</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
